/**
 * Chantier : -
 * Regle metier : USR-R03 (3 roles admin_serme/admin_client/operateur), USR-R04 (1 admin_client par etablissement, INVARIANT), USR-R06 (column-level masking), USR-R15 (anti-enumeration emails), USR-R18 (suppression d'un compte operateur sans activite)
 * Decision : D-AUTH-03 (EF invite-user), D-AUTH-05 (activation unifiee sur recovery : creation compte + lien recovery), D-USR-R03-NO-TRIGGER-V1, D-USR-R06-READ-GAP-ACCEPTED-V1, D-USR-R15-ANTI-ENUMERATION-V1, D-USR-SUPPR-01
 * Service applicatif Users : invite (RPC + EF), reactivate, deactivate (avec garde dernier
 * admin_client), suppression d'un compte sans activite (EF), transfert admin_client (atomique),
 * update profil. Pagination back-end avec filtres roles + recherche ilike echappee.
 */
import { supabase } from '@/lib/supabase';
import { escapePostgrestSearch } from '@/lib/escapePostgrestSearch';
import { AppError, EmailRateLimitError, EmailSendError, extractEdgeFunctionBody, extractEdgeFunctionError, handleSupabaseError } from '@/lib/errors';
import type { Enums } from '@/types/supabase';

// Types BDD (snake_case)
export interface UserRow {
  id: string;
  created_at: string;
  nom: string;
  prenom: string;
  email: string;
  telephone: string | null;
  role: Enums<'user_role'>;
  statut: Enums<'user_statut'>;
  etablissement_id: string | null;
}

// Types Front (camelCase)
export interface UserProfile {
  id: string;
  createdAt: string;
  nom: string;
  prenom: string;
  email: string;
  telephone: string | null;
  role: Enums<'user_role'>;
  statut: Enums<'user_statut'>;
  etablissementId: string | null;
}

export interface CreateUserInput {
  nom: string;
  prenom: string;
  email: string;
  telephone?: string;
  // Un admin SERME ne s'invite pas par ce chemin (USR-R03).
  role: Exclude<Enums<'user_role'>, 'admin_serme'>;
  etablissementId: string;
}

function mapRow(row: UserRow): UserProfile {
  return {
    id: row.id,
    createdAt: row.created_at,
    nom: row.nom,
    prenom: row.prenom,
    email: row.email,
    telephone: row.telephone,
    role: row.role,
    statut: row.statut,
    etablissementId: row.etablissement_id,
  };
}

export async function fetchUsers(etablissementId?: string): Promise<UserProfile[]> {
  let query = supabase
    .from('users')
    .select('*')
    .order('nom');

  if (etablissementId) {
    query = query.eq('etablissement_id', etablissementId);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data as UserRow[]).map(mapRow);
}

export interface FetchAllUsersParams {
  etablissementId?: string;
  search?: string;
  excludeRoles?: UserRow['role'][];
  page: number;
  pageSize: number;
}

// Pagination back-end : retourne `data` (page courante) + `count` (total apres
// filtres). Le filtre excludeRoles doit etre applique cote serveur, sinon le
// total et la taille de page deviennent incoherents (cf. design pagination).
export async function fetchAllUsers(
  params: FetchAllUsersParams,
): Promise<{ data: UserProfile[]; count: number }> {
  const { etablissementId, search, excludeRoles, page, pageSize } = params;
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let query = supabase
    .from('users')
    .select('*', { count: 'exact' });

  if (etablissementId) query = query.eq('etablissement_id', etablissementId);
  if (excludeRoles?.length) {
    query = query.not('role', 'in', `(${excludeRoles.join(',')})`);
  }
  if (search) {
    // Neutralise les caracteres reserves PostgREST/ILIKE avant interpolation dans `.or()`
    // (helper partage, cf. F-10).
    const s = escapePostgrestSearch(search);
    if (s) {
      query = query.or(`nom.ilike.%${s}%,prenom.ilike.%${s}%,email.ilike.%${s}%`);
    }
  }

  const { data, error, count } = await query.order('nom').range(from, to);
  if (error) throw error;
  return {
    data: (data as UserRow[]).map(mapRow),
    count: count ?? 0,
  };
}

/**
 * Chantier : -
 * Regle metier : USR-R14 (activation compte invite), USR-R15 (anti-enumeration)
 * Decision : D-AUTH-05 (activation unifiee sur recovery)
 * Envoie un lien de definition / reinitialisation de mot de passe via le mecanisme
 * recovery natif (resetPasswordForEmail). Point unique pour : 1re invitation, renvoi,
 * correction d'email, et « mot de passe oublie » (ForgotPasswordPage). Renvoyable a
 * l'infini. GoTrue repond 200 pour un email inconnu (anti-enumeration, USR-R15) ; un
 * `error` retourne concerne donc un compte existant : limite d'envoi (1/min par adresse
 * ou quota horaire) -> EmailRateLimitError, sinon panne serveur (SMTP refuse, timeout)
 * -> EmailSendError. L'appelant public doit masquer EmailRateLimitError (neutre).
 */
export async function sendPasswordSetupLink(email: string): Promise<void> {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  if (!error) return;
  if (error.code === 'over_email_send_rate_limit') throw new EmailRateLimitError(error.message);
  throw new EmailSendError(error.message);
}

/**
 * Chantier : -
 * Regle metier : USR-R14 (creation compte invite en_attente)
 * Decision : D-AUTH-05 (creation compte sans mot de passe + lien recovery)
 * Invitation utilisateur : (1) EF invite-user cree le compte Auth (createUser, confirme,
 * sans mot de passe) + UPSERT public.users en_attente ; (2) envoi du lien de definition de
 * mot de passe (recovery). Le user finalise via /reset-password (updateUser + mark_user_active).
 * Les deux etapes sont exposees separement pour l'onboarding d'etablissement, qui doit
 * distinguer un echec de creation (rollback) d'un echec d'envoi (compte cree, renvoi possible).
 */
export interface InviteResult {
  userId: string;
  email: string;
}

export async function inviteUser(input: CreateUserInput): Promise<InviteResult> {
  const result = await createInvitedUser(input);
  // Envoi du lien de definition de mot de passe (recovery, renvoyable). Cf. D-AUTH-05.
  await sendPasswordSetupLink(input.email);
  return result;
}

// Etape 1 seule : creation du compte via l'EF (idempotente pour un compte deja en_attente).
export async function createInvitedUser(input: CreateUserInput): Promise<InviteResult> {
  const { data, error } = await supabase.functions.invoke('invite-user', {
    body: {
      email: input.email,
      prenom: input.prenom,
      nom: input.nom,
      app_role: input.role,
      etablissement_id: input.etablissementId,
      telephone: input.telephone ?? undefined,
    },
  });

  if (error) throw new Error(await extractEdgeFunctionError(error));
  if (!data?.success) {
    throw new Error((data as { error?: string })?.error || 'Erreur lors de la creation du compte');
  }

  return {
    userId: (data as { user_id: string }).user_id,
    email: (data as { email: string }).email,
  };
}

/**
 * Chantier : -
 * Regle metier : USR-R14 (renvoi du lien d'activation), USR-R15 (anti-enumeration)
 * Decision : D-AUTH-05 (renvoi = lien recovery, renvoyable a l'infini)
 * Renvoi d'invitation : envoie un NOUVEAU lien de definition de mot de passe (recovery).
 * Ne recree PAS le compte et ne passe plus par l'EF invite-user (qui rejetait un email
 * deja existant -> 500). Le compte existe deja en statut en_attente.
 */
export async function resendInvitation(userId: string): Promise<void> {
  const { data: row, error: fetchError } = await supabase
    .from('users')
    .select('email, role')
    .eq('id', userId)
    .single();
  if (fetchError) throw fetchError;
  if (!row) throw new Error('Utilisateur introuvable');
  if (row.role === 'admin_serme') {
    throw new Error('Le renvoi d\'invitation n\'est pas supporte pour admin_serme');
  }

  await sendPasswordSetupLink(row.email);
}

export interface UpdatePendingUserInput {
  email: string;
  prenom: string;
  nom: string;
  telephone?: string;
}

export interface UpdatePendingUserResult {
  // D-ET-AUTO : false si le contact ET provisionne n'a pas pu suivre la correction (admin_client
  // uniquement). Absent pour un operateur.
  contactEtUpdated?: boolean;
  // Correction enregistree mais lien non envoye : message a afficher en avertissement (renvoi
  // depuis la fiche), comme `emailSendFailure` de l'onboarding.
  emailSendFailure?: string;
}

/**
 * Chantier : -
 * Regle metier : USR-R14 (correction d'un compte invite avant activation), REF-R11
 * Decision : D-AUTH-05, D-ET-AUTO (le contact ET provisionne suit la correction)
 * Edite un compte en statut 'en_attente' (email + nom/prenom/telephone) via l'EF
 * update-pending-user (service_role : updateUserById pour l'email). Si l'email a change,
 * renvoie le lien de definition de mot de passe (recovery) a la nouvelle adresse.
 */
export async function updatePendingUser(userId: string, input: UpdatePendingUserInput): Promise<UpdatePendingUserResult> {
  const { data, error } = await supabase.functions.invoke('update-pending-user', {
    body: {
      user_id: userId,
      email: input.email,
      prenom: input.prenom,
      nom: input.nom,
      telephone: input.telephone ?? undefined,
    },
  });

  if (error) throw new Error(await extractEdgeFunctionError(error));
  if (!data?.success) {
    throw new Error((data as { error?: string })?.error || 'Erreur lors de la mise a jour du compte');
  }

  // Renvoi du lien uniquement si l'email a change (sinon le compte etait deja joignable).
  // Un echec d'envoi ne leve pas : la correction est faite, un nouvel « Enregistrer » ne
  // renverrait rien (l'email est deja le bon en base).
  const body = data as { email_changed?: boolean; contact_et_updated?: boolean };
  if (body.email_changed) {
    try {
      await sendPasswordSetupLink(input.email);
    } catch (err) {
      if (!(err instanceof AppError)) throw err;
      return { contactEtUpdated: body.contact_et_updated, emailSendFailure: err.userMessage };
    }
  }
  return { contactEtUpdated: body.contact_et_updated };
}

// Desactivation ou reactivation par l'EF user-access : RPC safe_*_user (avec le JWT de l'appelant) puis
// bannissement ou levee du bannissement dans Supabase Auth. Renvoie l'erreur (message + code PG de la RPC
// si c'est elle qui a refuse), null en cas de succes.
async function invokeUserAccess(
  action: 'deactivate' | 'reactivate',
  userId: string,
): Promise<{ message: string; code?: string } | null> {
  const { error } = await supabase.functions.invoke('user-access', {
    body: { action, user_id: userId },
  });
  if (!error) return null;
  const body = await extractEdgeFunctionBody(error);
  const message = typeof body?.error === 'string' ? body.error : await extractEdgeFunctionError(error);
  // Reponse de l'EF sans code PG : etape Auth en echec (bannissement), panne remontee a Sentry.
  if (body && !body.code) throw new AppError(message, 'unknown', message);
  return { message, code: body?.code as string | undefined };
}

/**
 * Chantier : Lot securite ASVS L2 n.2 (V7.4.2)
 * Regle metier : USR-R04 (1 admin_client actif par etablissement, INVARIANT, BLOQUANT), USR-R13 (coupure de l'acces)
 * Decision : D-SEC-DEACTIV-BAN
 * Desactivation (garde "dernier admin_client" dans la RPC) puis bannissement du compte : il ne peut plus se
 * connecter ni renouveler sa session. Echec "dernier admin" explicite avec hint UX (transferAdminClient avant).
 */
export async function deactivateUser(userId: string): Promise<void> {
  const error = await invokeUserAccess('deactivate', userId);
  if (!error) return;

  // Cas metier "dernier admin actif" : message PG long enrichi en AppError UX
  // pour expliquer la marche a suivre (promouvoir un autre user via transfer_admin_client).
  if (error.message.includes('dernier administrateur')) {
    throw new AppError(
      error.message,
      'validation',
      'Impossible de desactiver le dernier administrateur de cet etablissement. Promouvez d\'abord un autre utilisateur en administrateur avant de pouvoir desactiver celui-ci.',
    );
  }
  // Refus de la RPC : traduction PG habituelle. Sinon erreur reseau.
  if (error.code) handleSupabaseError({ message: error.message, code: error.code });
  throw new Error(error.message);
}

// Reactiver un utilisateur (symetrique : RPC safe_reactivate_user puis levee du bannissement).
export async function reactivateUser(userId: string): Promise<void> {
  const error = await invokeUserAccess('reactivate', userId);
  if (error) throw new Error(error.message);
}

/**
 * Chantier : -
 * Regle metier : USR-R18 (suppression d'un compte operateur sans activite)
 * Decision : D-USR-SUPPR-01
 * Suppression definitive via l'EF delete-user (admin_serme) : identite Auth puis ligne users.
 * Le serveur refuse (409, message lisible) un autre role ou un compte qui a une activite :
 * la desactivation reste alors la voie. Apres un echec partiel (500), la ligne reste listee :
 * relancer la suppression la termine.
 */
export async function deleteUser(userId: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke('delete-user', {
    body: { user_id: userId },
  });

  if (error) throw new Error(await extractEdgeFunctionError(error));
  if (!data?.success) {
    throw new Error((data as { error?: string })?.error || 'Erreur lors de la suppression du compte');
  }
}

/**
 * Chantier : -
 * Regle metier : USR-R04 (1 admin_client actif par etablissement)
 * Decision : -
 * Transfert atomique du role admin_client : ancien -> operateur, nouveau -> admin_client
 * dans la meme transaction PG (preserve l'invariant 1 admin par etablissement).
 */
// Transfert atomique du role admin_client
export async function transferAdminClient(
  etablissementId: string,
  newAdminId: string
): Promise<void> {
  const { error } = await supabase.rpc('transfer_admin_client', {
    p_etablissement_id: etablissementId,
    p_new_admin_id: newAdminId,
  });

  if (error) throw error;
}
